import { useEffect } from 'react';
import { format } from 'date-fns';
import { useAuthStore } from '@/store/auth.store';
import { VendorHomeBand } from '@/components/dashboard/VendorHomeBand';
import { ComplianceHomeBand } from '@/components/dashboard/ComplianceHomeBand';
import { RiskResponseHomeBand } from '@/components/dashboard/RiskResponseHomeBand';

// Home Page BI — a module-composable tenant home. The page composes a flat, ordered list of
// sections and renders only the ones this organization owns. TWO SEPARATE gates, never conflated:
//   • Vendor / Compliance are ENTITLEMENT-gated (entitledModules).
//   • Risk Response is ALWAYS-ON, ROLE-gated (never filtered through entitledModules).
// The gate PREVENTS THE MOUNT — each band's data hook lives inside the band, so an unentitled or
// retired module (EASM, RET-7 — simply not in the list) is never mounted and costs no request.
// Order: greeting → Vendor → Compliance → Risk Response.

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

export function DashboardPage() {
  useEffect(() => {
    document.title = 'Home — VeriGRC';
  }, []);

  const entitledModules = useAuthStore((s) => s.entitledModules);
  const user = useAuthStore((s) => s.user);
  // Risk Response is role-gated only — mirrors the attention-tile visibility (never an entitlement).
  const canSeeRiskResponse = useAuthStore((s) => s.hasAnyRole(['risk_manager', 'super_admin', 'admin']));

  const isEntitled = (m: string) => entitledModules.includes(m);
  // First name only when set; otherwise the greeting stands alone — never fall back to email.
  const firstName = user?.firstName?.trim() ?? '';

  const nothingToShow =
    !isEntitled('vrm') && !isEntitled('compliance') && !canSeeRiskResponse;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-[14px]">
        <span className="text-[21px] font-semibold tracking-[-0.01em] text-text-primary">
          {greeting()}{firstName ? `, ${firstName}` : ''}
        </span>
        <span className="ml-auto text-[11px] text-[#5C7189]">{format(new Date(), 'EEEE, d MMMM yyyy')}</span>
      </div>

      {/* Entitlement-gated (renders only when entitled → the band's hook only fires then). */}
      {isEntitled('vrm') && <VendorHomeBand />}
      {isEntitled('compliance') && <ComplianceHomeBand />}
      {/* Always-on, role-gated — never filtered through entitledModules. */}
      {canSeeRiskResponse && <RiskResponseHomeBand />}

      {nothingToShow && (
        <div
          className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-[#2A3F5C] bg-[#111E30] p-[44px_24px] text-center"
          data-testid="home-none-state"
        >
          <span className="text-[13.5px] font-semibold text-[#B7C8DA]">Nothing to show here yet</span>
          <span className="max-w-[48ch] text-[12px] leading-[1.6] text-[#7A8CA3] [text-wrap:pretty]">
            Your home page assembles from the modules your organization is licensed for. None are enabled for your account yet.
          </span>
        </div>
      )}
    </div>
  );
}
